// Starts <background-video> tag helper output ([data-background-video]). The <video> is rendered
// with preload="none" and no autoplay attribute (see BackgroundVideoTagHelper.cs) so the
// multi-MB video doesn't download during page load and compete with the poster (the hero's LCP
// element), CSS and fonts. Playback starts only after the window load event, and only once the
// video is near the viewport - a background video further down the page doesn't download until
// someone scrolls to it. With prefers-reduced-motion: reduce it never starts; the poster stays.
//
// The video is transparent until its first "playing" event, which marks it data-playing so
// atoms/background-video.css fades it in over the poster <img> underneath.

const ROOT_MARGIN = "200px";

const started = new WeakSet<HTMLVideoElement>();

function play(video: HTMLVideoElement): void {
    if (!started.has(video)) {
        started.add(video);
        video.addEventListener("playing", () => video.setAttribute("data-playing", ""), { once: true });
        video.muted = true; // Autoplay policies only allow unprompted playback of muted video.
        video.preload = "auto";
    }
    video.play().catch(() => {
        // Autoplay blocked (e.g. data saver/low power mode) - the poster stays, which is fine
        // for decorative motion.
    });
}

// Plays near the viewport and pauses again out of it, so a looping video someone has scrolled
// past doesn't keep decoding (CPU, battery) for the rest of the visit.
function observe(videos: HTMLVideoElement[]): void {
    const observer = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                const video = entry.target as HTMLVideoElement;
                if (entry.isIntersecting) {
                    play(video);
                } else if (started.has(video)) {
                    video.pause();
                }
            }
        },
        { rootMargin: ROOT_MARGIN },
    );

    for (const video of videos) {
        observer.observe(video);
    }
}

// Umbraco Engage tracks every <video> as one a visitor watches, so a background video's scripted
// play() and pause() (after load, and in and out of view) were logged as Play/Pause and skewed the
// video report. Engage has no opt-out for video (engage-no-tracking is only read for forms), but it
// skips any element it has already marked as tracked (__engageTracked, IsElementTrackedByEngage in
// umbracoEngage.analytics.js). main.js runs before Engage's deferred script (Layout.cshtml), so
// marking the videos here comes first. Undocumented: tests/e2e/engage-video.spec.ts checks it -
// run it after every Engage upgrade.
const ENGAGE_TRACKED = "__engageTracked";

function hideFromEngage(videos: HTMLVideoElement[]): void {
    for (const video of videos) {
        Reflect.set(video, ENGAGE_TRACKED, true);
    }
}

export function initBackgroundVideo(): void {
    const videos = Array.from(document.querySelectorAll<HTMLVideoElement>("video[data-background-video]"));
    hideFromEngage(videos);
    if (videos.length === 0 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
    }

    if (document.readyState === "complete") {
        observe(videos);
    } else {
        window.addEventListener("load", () => observe(videos), { once: true });
    }
}
